// A course recording's thumbnail (decision 47, as Adam changed it on 12 Oct): every course recording
// (The Funding Engine, Profit On Your Terms, the Accelerator, Property Cashflow For Life) can have
// Adam's own 16:9 design, uploaded in admin exactly as a webinar's, in the private covers bucket
// (hub.recordings.thumbnail_path, migration 20261012200000). Pure: lib/format.js and the product's
// model.js only (and the on-demand course's, for its module panel), so a test imports it.
// RecordingThumb.jsx draws the frame and free/thumbnails.js signs the links, through
// sign-file's recordings target, 20 a call, read with the member's own login, so row level security
// decides which come back.
//
// The rule for a list of recording cards: while none of a product's recordings has a thumbnail, its
// cards keep the boards' own look, exactly as before (as Coming up's cards do for webinars, Adam, 9
// Oct); once 1 has, every recording card of that product draws the 16:9 frame, with the picture, or
// the grey date panel where it has none, so cards side by side keep 1 height. The panel's date is
// their own intake's session for a week's recording (decision 52), else the day it was made. The
// frame is drawn in the product's own look (product.css, --pp-panel).
//
// A recording's picture shows to a member only once that recording is open to them (Adam, 12 Oct):
// switched on, published, and released to their intake or group under the product's release rules.
// Until then its card shows the grey date panel (their own session's date where the card has 1), so a
// week's frame never shows a recording they can't play yet, and View as shows exactly that. Open is
// the database's own answer (isReleased: its content comes back only then), and sign-file signs only
// those for a member or View as too, so the picture can't be had another way.
import * as f from '../lib/format.js'
import { isReleased, recordingForModule, sessionForModule } from './model.js'
import { numberedOf } from '../products/ondemand/model.js'

// What free/thumbnails.js signs for a recording: its id and its path, only once it's open to them
// (open: isReleased, or the on-demand course's row.ready, the same answer); null where it isn't, or
// there's no recording
export function thumbItem(rec, open) {
  return rec && rec.id && open === true ? { id: rec.id, thumbnailPath: rec.thumbnail_path || null } : null
}

// Whether any of these recordings has a thumbnail: the product then draws the frame on every card
export function anyThumbnail(recs) {
  return (recs || []).some(r => Boolean(r?.thumbnail_path))
}

// The date on a recording's grey panel: the day it was recorded, else its session's start, else the
// day it went up; null where it has none of them (the panel then shows empty)
export function recordingMs(rec, sessions = []) {
  if (!rec) return null
  if (rec.recorded_on) return f.calendarDay(rec.recorded_on)
  const s = rec.session_id ? (sessions || []).find(x => x.id === rec.session_id) : null
  if (s?.starts_at) return Date.parse(s.starts_at)
  return rec.published_at ? Date.parse(rec.published_at) : null
}

export function recordingBlock(rec, sessions = []) {
  const t = recordingMs(rec, sessions)
  return t == null || (typeof t === 'number' && !Number.isFinite(t)) ? null : f.dateBlock(t)
}

// A card's frame: null while the product has no thumbnail anywhere (the board's own look), else
// { item, block }: the recording to sign (item null where there's no recording yet, or it isn't open
// to them, so the panel shows) and the date for its panel. open: whether the recording is open to them
// (isReleased); left out, it isn't, so no picture shows by mistake
export function cardThumb(rec, { any, sessions = [], block = null, open = false } = {}) {
  if (!any) return null
  const item = thumbItem(rec, open)
  return { item: item && item.thumbnailPath ? item : null, block: block || recordingBlock(rec, sessions) }
}

// A course recording's frame on a card that shows it alone (the Recordings page, Catch up first, the
// search): a recording in a week or module takes the date of their own intake's session for it, as the
// home's weeks do (weekThumb), since a week's recording can be 1 an earlier intake made and opens to
// each intake after its own session (decision 52); only where they have no session for it (no intake)
// the recording's own date
export function recordingThumb(data, rec, any) {
  const own = rec?.module_id ? sessionBlock(sessionForModule(data, rec.module_id)) : null
  return cardThumb(rec, { any, sessions: data?.sessions, block: own, open: Boolean(rec?.id) && isReleased(data, rec.id) })
}

// The items to sign for a page's cards ([cardThumb ...]), each path once
export function itemsOf(cards) {
  const out = []
  const seen = new Set()
  for (const c of cards || []) {
    const i = c?.item
    if (!i || !i.thumbnailPath || seen.has(i.thumbnailPath)) continue
    seen.add(i.thumbnailPath)
    out.push(i)
  }
  return out
}

// The header search's hits (library/search.js and lib/tagResults.js, { label, where, href }) that
// open a recording: its page is <product path>/<recording key>, or <product path>/<module key> for a
// week or module, a chapter's with ?t= after it. The Library's own rows say which recording that is.
// Only products with a thumbnail anywhere are in the index, so the rest of the hits look as before.
// Property Cashflow For Life's hits keep the course's own module panel where there's no picture, as its
// cards do (products/ondemand/parts.jsx Thumb; Adam, 12 Oct): module is its side of the pentagon (the
// module's place among the numbered ones, -1 for the Introduction) and whether the recording is open to
// them, which dims the mark as Thumb does for 1 coming soon.
//   searchThumbIndex(d, products)   Map of a recording page's path to { item, block, productId, module? }
//   thumbForHit(hit, index)         that hit's { item, block, productId, module? }, or null
export function searchThumbIndex(d, products) {
  const out = new Map()
  const recs = d?.recordings || []
  for (const p of products || []) {
    // the course recordings only (decision 47): never the video, whose page is its own
    if (!p?.path || p.type === 'masterclass') continue
    const own = recs.filter(r => r.product_id === p.id)
    if (!anyThumbnail(own)) continue
    const ordered = own.slice().sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0))
    const numbered = p.type === 'on_demand' ? numberedOf({ modules: (d.modules || []).filter(x => x.product_id === p.id) }) : null
    const frame = r => {
      const card = { ...recordingThumb(d, r, true), productId: p.id }
      // the module's side of the pentagon only on Property Cashflow For Life's 5 Ms: another on-demand
      // course's module panel has no mark (pentagon false; the review of 13 Oct)
      if (numbered) {
        card.module = { index: numbered.findIndex(m => m.id === r.module_id), open: Boolean(r.id) && isReleased(d, r.id) }
        if (p.slug !== 'cashflow') card.module.pentagon = false
      }
      return card
    }
    for (const m of (d.modules || []).filter(x => x.product_id === p.id && x.key)) {
      const r = ordered.find(x => x.module_id === m.id)
      if (r) out.set(`${p.path}/${m.key}`, frame(r))
    }
    for (const r of ordered) {
      if (r.key) out.set(`${p.path}/${r.key}`, frame(r))
    }
  }
  return out
}

export function thumbForHit(hit, index) {
  if (!hit?.href || !index || !index.size) return null
  const path = String(hit.href).split(/[?#]/)[0]
  return index.get(path) || null
}

// A session's date for the panel: its start
export function sessionBlock(session) {
  const t = session?.starts_at ? Date.parse(session.starts_at) : NaN
  return Number.isFinite(t) ? f.dateBlock(t) : null
}

// A week's frame on a course home's "Your 6 weeks" (The Funding Engine's and Profit On Your Terms's):
// the recording its page plays first, with the date of their own intake's session for the week on the
// panel, else the recording's own date; the panel alone for a week with no recording yet. A week with
// nothing open to them yet still has a recording its page names (recordingForModule falls back to the
// first), but its picture waits until that recording is open to them
export function weekThumb(data, moduleId, any) {
  if (!any) return null
  const rec = recordingForModule(data, moduleId)
  const open = Boolean(rec?.id) && isReleased(data, rec.id)
  return cardThumb(rec, { any, sessions: data?.sessions, block: sessionBlock(sessionForModule(data, moduleId)), open })
}
